/**
 * Return Assistant Demo — "I want to return something"
 * Guided flow: order # → reason → item condition → policy & next steps
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack as BackIcon, LocalShipping as ShipIcon } from '@mui/icons-material';

const RETURN_REASONS = ['Changed mind', 'Wrong size', 'Defective', 'Not as described', 'Late delivery', 'Other'];
const CONDITIONS = ['Unopened', 'Opened, unused', 'Used, like new', 'Used, noticeable wear', 'Damaged'];

export default function ReturnAssistantDemo({ onBack }) {
  const [step, setStep] = useState(0);
  const [orderId, setOrderId] = useState('');
  const [reason, setReason] = useState('');
  const [condition, setCondition] = useState('');
  const [withinWindow, setWithinWindow] = useState('yes');

  const steps = ['Order info', 'Reason', 'Condition'];
  const canProceed = step === 0 ? orderId : step === 1 ? reason : condition;

  const eligible = withinWindow === 'yes' && !condition?.toLowerCase().includes('damaged');
  const refundType = condition?.toLowerCase().includes('unopened') ? 'Full refund' : 'Partial refund or exchange';

  return (
    <Paper sx={{ p: 3, maxWidth: 520, mx: 'auto' }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <Button size="small" startIcon={<BackIcon />} onClick={onBack}>
              Back
            </Button>
          )}
          <Typography variant="h6" sx={{ flex: 1 }}>
            Return Assistant
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          We&apos;ll guide you through the return process and explain your options.
        </Typography>

        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box>
            <TextField
              fullWidth
              label="Order number"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="e.g. ORD-12345"
              sx={{ mb: 2 }}
            />
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>Within 30-day return window?</Typography>
              <RadioGroup row value={withinWindow} onChange={(e) => setWithinWindow(e.target.value)}>
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {step === 1 && (
          <FormControl fullWidth>
            <InputLabel>Reason for return</InputLabel>
            <Select value={reason} label="Reason for return" onChange={(e) => setReason(e.target.value)}>
              {RETURN_REASONS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step === 2 && (
          <FormControl fullWidth>
            <InputLabel>Item condition</InputLabel>
            <Select value={condition} label="Item condition" onChange={(e) => setCondition(e.target.value)}>
              {CONDITIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step < 2 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!canProceed}>
            Next
          </Button>
        ) : (
          <Box>
            {eligible ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Your return appears eligible. You may receive a {refundType}.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {withinWindow === 'no'
                  ? 'Outside the standard return window. Contact support for exceptions.'
                  : 'Item condition may affect refund eligibility. A support agent can help.'}
              </Alert>
            )}
            <Typography variant="subtitle2" gutterBottom>Next steps</Typography>
            <Stack spacing={1}>
              <Typography variant="body2">1. Print the return label (we&apos;ll email it)</Typography>
              <Typography variant="body2">2. Pack the item securely</Typography>
              <Typography variant="body2">3. Drop off at any authorized carrier</Typography>
              <Typography variant="body2">4. Refund processed within 5–7 business days of receipt</Typography>
            </Stack>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="contained" startIcon={<ShipIcon />}>
                Get return label (demo)
              </Button>
              <Button variant="outlined" onClick={() => { setStep(0); setOrderId(''); setReason(''); setCondition(''); setWithinWindow('yes'); }}>
                Start over
              </Button>
            </Box>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              This is a demo. In production, this would integrate with your order system.
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
